import { Suspense, lazy } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { resolvePmsLinkSlug } from './resolvePmsLinkSlug';

/** Loaded through the MF runtime, never at build time (root CLAUDE.md rule 1). */
const PmsRmTokenForm = lazy(() => import('clarixPms/RmTokenForm'));

/**
 * The PUBLIC manager link — `/pms/eval/rm/:token` (PMS D-27).
 *
 * The evaluator's counterpart to `/pms/eval/:token`, and mounted outside
 * `RequireAuth` for the same reason: a manager may be a directory-only membership
 * with no Clarix account (`user_id = null`), and D-15 already struck the login
 * clause for the employee side.
 *
 * The `rm` segment is literal and sits BEFORE the token, so this route and the
 * employee's differ by segment count and cannot shadow one another — a token is
 * always the last segment on both.
 */
export function ManagerEvaluationLinkPage() {
  const { token } = useParams<{ token: string }>();
  const [search] = useSearchParams();
  const slug = resolvePmsLinkSlug(search.get('ws'));

  if (!slug || !token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-muted/30 px-4">
        <div className="w-full max-w-md rounded-xl border border-border bg-background p-8 text-center shadow-sm">
          <h1 className="mb-2 text-lg font-semibold text-foreground">This link is not valid</h1>
          <p className="text-sm text-muted-foreground">
            {token
              ? 'This link does not say which workspace it belongs to. Open it from your email, which carries the full address.'
              : 'Open the most recent email and use the link exactly as it appears there.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <Suspense
      fallback={
        <div className="flex min-h-screen items-center justify-center bg-muted/30">
          <span className="text-sm text-muted-foreground">Opening your team evaluations…</span>
        </div>
      }
    >
      <PmsRmTokenForm slug={slug} token={token} />
    </Suspense>
  );
}
